import React, {useEffect, useState} from 'react';
import {classes} from './ItemDetails.st.css';
import {clickOnShowMoreBi} from '../../../../../../types/app.types';
import {useControllerProps} from '../../../../../../domain/controllers/ControllerContext';
import {TextButton, TextButtonPriority} from 'wix-ui-tpa/cssVars';
import ChevronDown from 'wix-ui-icons-common/on-stage/ChevronDown';
import ChevronUp from 'wix-ui-icons-common/on-stage/ChevronUp';
import {DetailLine} from './DetailLine';
import {DetailsGroup} from './DetailsGroup';

export enum CartItemOptionsDataHook {
  CollapseExpandButton = 'CartItemOptionsDataHook.collapseExpandButton',
  GroupName = 'CartItemOptionsDataHook.groupName',
  GroupOptionsList = 'CartItemOptionsDataHook.groupOptionsList',
}

export interface OptionsProps {
  dataHook: string;
  itemId: string;
  details: {
    descriptionLines?: string[];
    groups?: {descriptionLines: string[]; groupName?: string}[];
  };
  maxOptionValLength: number;
  collapseSettings: {
    numberOfOptionsToCollapseAfter: number;
    showMoreTitle: string;
    showLessTitle: string;
  };
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const ItemDetails = ({itemId, details, collapseSettings, dataHook}: OptionsProps) => {
  const {supportModifiersOnCart, reportClickToOpenCloseMoreDetailsBi} = useControllerProps().cartStore;
  const {numberOfOptionsToCollapseAfter, showMoreTitle, showLessTitle} = collapseSettings;
  const descriptionLines = details.descriptionLines;
  const groups = details.groups;
  const numberOfDescriptionLines = descriptionLines?.length || 0;

  const [shouldCollapseAndExpand] = useState(
    Boolean(numberOfDescriptionLines > numberOfOptionsToCollapseAfter || (supportModifiersOnCart && !!groups?.length))
  );
  const [numberOfOptionsToShow, setNumberOfOptionsToShow] = useState(
    shouldCollapseAndExpand ? numberOfOptionsToCollapseAfter : numberOfDescriptionLines
  );
  const [isCollapsed, setIsCollapsed] = useState(shouldCollapseAndExpand);

  const onCollapseExpandClick = () => {
    if (supportModifiersOnCart) {
      reportClickToOpenCloseMoreDetailsBi({
        lineItemId: itemId,
        action: isCollapsed ? clickOnShowMoreBi.OPEN : clickOnShowMoreBi.CLOSE,
      });
    }
    setIsCollapsed(!isCollapsed);
  };

  useEffect(() => {
    setNumberOfOptionsToShow(isCollapsed ? numberOfOptionsToCollapseAfter : numberOfDescriptionLines);
  }, [isCollapsed, numberOfDescriptionLines, numberOfOptionsToCollapseAfter, details]);

  const renderCollapseExpandButton = () => {
    return (
      <TextButton
        className={classes.collapseButton}
        suffixIcon={isCollapsed ? <ChevronDown /> : <ChevronUp />}
        priority={TextButtonPriority.secondary}
        data-hook={CartItemOptionsDataHook.CollapseExpandButton}
        onClick={onCollapseExpandClick}>
        {isCollapsed ? showMoreTitle : showLessTitle}
      </TextButton>
    );
  };

  const shouldShowDetailsList = !!descriptionLines?.length || !isCollapsed;

  return (
    <div className={classes.root}>
      {shouldShowDetailsList && (
        <ul className={classes.options} data-hook={dataHook}>
          {descriptionLines?.slice(0, numberOfOptionsToShow).map((descriptionLine) => (
            <li key={`option-${itemId}-${descriptionLine}`}>
              <DetailLine value={descriptionLine} />
            </li>
          ))}
          {supportModifiersOnCart &&
            !isCollapsed &&
            groups?.map((group) => (
              <li key={`option-${itemId}-${group.groupName}`} className={classes.group}>
                <DetailsGroup itemId={itemId} group={group} />
              </li>
            ))}
        </ul>
      )}
      {shouldCollapseAndExpand && renderCollapseExpandButton()}
    </div>
  );
};
